import React, { useState } from 'react';
import { Button, CircularProgress, Snackbar, Alert } from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../../app/store';
import { useSavePolicyMutation } from '../../../../services/CaseManagementService';

const SavePolicyButton: React.FC = () => {
  const navigate = useNavigate();
  const policy = useAppSelector((state) => state.policy.currentPolicy);
  const selectedCompany = useAppSelector((state) => state.policy.selectedCompany);
  const [savePolicyMutation, { isLoading }] = useSavePolicyMutation();
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleSave = async () => {
    if (!policy) {
      setNotification({
        open: true,
        message: 'No policy data to save',
        severity: 'error',
      });
      return;
    }

    // Basic validation
    if (!policy.policyNumber?.trim()) {
      setNotification({
        open: true,
        message: 'Policy number is required',
        severity: 'error',
      });
      return;
    }

    if (!policy.policyName?.trim()) {
      setNotification({
        open: true,
        message: 'Policy name is required',
        severity: 'error',
      });
      return;
    }

    try {
      // Generate a new GUID for new policies
      const policyId = policy.id && policy.id.trim() !== '' ? policy.id : crypto.randomUUID();
      
      const result = await savePolicyMutation({
        PolicyView: {
          ...policy,
          id: policyId,
          company: selectedCompany || policy.company, // Use selectedCompany from query param
        },
      }).unwrap();

      setNotification({
        open: true,
        message: 'Policy saved successfully!',
        severity: 'success',
      });

      // Navigate back to policies list after successful save
      setTimeout(() => {
        const queryParam = selectedCompany ? `?company=${encodeURIComponent(selectedCompany)}` : '';
        navigate(`/case-management/policies${queryParam}`);
      }, 1500);
    } catch (error: any) {
      console.error('Error saving policy:', error);
      setNotification({
        open: true,
        message: error?.data?.message || 'Failed to save policy',
        severity: 'error',
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handleSave}
        disabled={isLoading}
        startIcon={isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
        sx={{
          backgroundColor: '#162346',
          color: 'white',
          fontWeight: 600,
          px: 3,
          '&:hover': {
            backgroundColor: '#1a2a52',
          },
          '&:disabled': {
            backgroundColor: '#64748b',
            color: 'white',
          },
        }}
      >
        {isLoading ? 'Saving...' : 'Save Policy'}
      </Button>

      <Snackbar
        open={notification.open}
        autoHideDuration={notification.severity === 'success' ? 4000 : 6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SavePolicyButton;
